/**
 * @fileoverview Civil & Criminal Offences — master agent chat (shared ChatSheet)
 * @module jobs/phases/review/civilCriminalOffences/OffenceMasterChatSheet
 *
 * Uses the same premium slide-out `ChatSheet` as the Recoverable Transactions
 * master chat (full-screen expand + draggable pop-out window) instead of the
 * old fixed bottom-right dock. Messages come from `listMasterChat`, sends go
 * through `sendMasterChatMessage`, and the thinking indicator reflects the
 * master task running state plus the optimistic pending message.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

export function OffenceMasterChatSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: Id<"jobs">;
  agentBusy: boolean;
  canSend: boolean;
}) {
  const { open, onOpenChange, jobId, agentBusy, canSend } = props;
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const { data: chatMessages } = useQuery(
    convexQuery(offences.publicQueries.listMasterChat, { jobId }),
  );

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(
      offences.publicMutations.sendMasterChatMessage,
    ),
  });

  const realMessages: ChatMessageData[] = useMemo(
    () =>
      (
        (chatMessages ?? []) as Array<{
          _id: string;
          role: string;
          body: string;
          createdAt: number;
        }>
      )
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: m._id,
          role: m.role as "user" | "assistant",
          content: m.body,
          createdAt: m.createdAt,
        })),
    [chatMessages],
  );

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  useEffect(() => {
    if (!pendingMessage) return;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const lastMessage = messages[messages.length - 1];
  const isThinking =
    agentBusy ||
    isPending ||
    (lastMessage?.role === "user" && pendingMessage !== null) ||
    (lastMessage?.role === "user" && agentBusy);

  const handleSend = async (raw: string) => {
    const body = raw.trim();
    if (!body || !canSend) return;
    setPendingMessage(body);
    try {
      await sendMessage({ jobId, body });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
      setPendingMessage(null);
    }
  };

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Civil & Criminal Offences"
      description="Coordinate the offence-head agents: run or refresh them, ask about findings, or route an instruction to a specific head."
      messages={messages}
      onSend={(m) => void handleSend(m)}
      userInfo={userInfo}
      isSending={isPending}
      isThinking={isThinking}
      emptyMessage={
        'Ask about a head’s outcome, a respondent’s exposure across heads, or say "run all" to refresh every head.'
      }
      inputPlaceholder={
        canSend
          ? "Instruct or ask the offences master…"
          : "Viewers can't chat with the agent"
      }
    />
  );
}
